import { jsPDF as JsPdf } from 'jspdf';
import { KEEP_STATIC_CODES_SAFE, THESE_ARE_YOUR_NEW_BACKUP_CODES, USE_BACKUP_CODES } from 'common/constants/auth';
import { Logger } from 'utilities';
import {
  MM_PER_POINT,
  PDF_CODE_FONT_FAMILY,
  PDF_CODE_FONT_SIZE_PT,
  PDF_CODE_GUTTER_MM,
  PDF_CODE_INDEX_FONT_SIZE_PT,
  PDF_CODE_LINE_HEIGHT_MM,
  PDF_CODES_BOX_CORNER_RADIUS_MM,
  PDF_CODES_BOX_PADDING_MM,
  PDF_CONTENT_WIDTH_MM,
  PDF_FONT_STYLE_BOLD,
  PDF_FONT_STYLE_NORMAL,
  PDF_LINE_HEIGHT_RATIO,
  PDF_LOGO_FORMAT,
  PDF_LOGO_NATURAL_HEIGHT_PX,
  PDF_LOGO_NATURAL_WIDTH_PX,
  PDF_LOGO_SPACING_MM,
  PDF_LOGO_URL,
  PDF_LOGO_WIDTH_MM,
  PDF_PAGE_FORMAT,
  PDF_PAGE_MARGIN_MM,
  PDF_PAGE_ORIENTATION,
  PDF_SUBTITLE_FONT_SIZE_PT,
  PDF_SUBTITLE_SPACING_MM,
  PDF_SUPPORTING_TEXT_FONT_SIZE_PT,
  PDF_SUPPORTING_TEXT_SPACING_MM,
  PDF_TEXT_FONT_FAMILY,
  PDF_TITLE_FONT_SIZE_PT,
  PDF_TITLE_SPACING_MM,
  PDF_UNIT,
} from './BackupCodes.constants';
import type { DownloadBackupCodesPdfParams } from './BackupCodes.props';
import { PDF_BODY_TEXT_COLOR, PDF_MUTED_TEXT_COLOR, PDF_PRIMARY_COLOR } from './BackupCodes.styles';

export const getBackupCodesFileName = () =>
  `scalar-backup-codes-${new Date().toLocaleDateString('en-US').replace(/\//g, '-')}.pdf`;

const includeUsername = (username: string | null | undefined) => {
  if (username) {
    return ` for ${username}`;
  }

  return '';
};

// Converts a font size in points to a comfortable line height in millimeters.
const ptToLineHeightMm = (fontSizePt: number) => fontSizePt * MM_PER_POINT * PDF_LINE_HEIGHT_RATIO;

// A missing logo must never block the download: the codes themselves are what the user needs.
const loadLogoDataUrl = async (): Promise<string | null> => {
  try {
    const response = await fetch(PDF_LOGO_URL);

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();

    return await new Promise<string | null>(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    Logger.warn('Could not load the Scalar logo for the backup codes PDF', error);
    return null;
  }
};

export const downloadBackupCodesPdf = async ({ backupCodes, username = '' }: DownloadBackupCodesPdfParams) => {
  const doc = new JsPdf({ format: PDF_PAGE_FORMAT, orientation: PDF_PAGE_ORIENTATION, unit: PDF_UNIT });
  const logoDataUrl = await loadLogoDataUrl();

  let cursorY = PDF_PAGE_MARGIN_MM;

  const drawParagraph = (text: string, fontSizePt: number) => {
    doc.setFontSize(fontSizePt);
    doc.splitTextToSize(text, PDF_CONTENT_WIDTH_MM).forEach((line: string) => {
      doc.text(line, PDF_PAGE_MARGIN_MM, cursorY);
      cursorY += ptToLineHeightMm(fontSizePt);
    });
  };

  // The logo gets its own full-width row so the title can never sit flush against it.
  if (logoDataUrl) {
    const logoHeight = PDF_LOGO_WIDTH_MM * (PDF_LOGO_NATURAL_HEIGHT_PX / PDF_LOGO_NATURAL_WIDTH_PX);
    doc.addImage(logoDataUrl, PDF_LOGO_FORMAT, PDF_PAGE_MARGIN_MM, cursorY, PDF_LOGO_WIDTH_MM, logoHeight);
    cursorY += logoHeight + PDF_LOGO_SPACING_MM;
  }

  doc.setTextColor(PDF_BODY_TEXT_COLOR);
  doc.setFont(PDF_TEXT_FONT_FAMILY, PDF_FONT_STYLE_BOLD);
  drawParagraph(
    `${THESE_ARE_YOUR_NEW_BACKUP_CODES}${includeUsername(username)}`,
    PDF_TITLE_FONT_SIZE_PT
  );
  cursorY += PDF_TITLE_SPACING_MM;

  doc.setFont(PDF_TEXT_FONT_FAMILY, PDF_FONT_STYLE_NORMAL);
  drawParagraph(KEEP_STATIC_CODES_SAFE, PDF_SUBTITLE_FONT_SIZE_PT);
  cursorY += PDF_SUBTITLE_SPACING_MM;

  drawParagraph(USE_BACKUP_CODES, PDF_SUPPORTING_TEXT_FONT_SIZE_PT);
  cursorY += PDF_SUPPORTING_TEXT_SPACING_MM;

  // One code per line in a single column
  const boxHeight = PDF_CODES_BOX_PADDING_MM * 2 + Math.max(backupCodes.length, 1) * PDF_CODE_LINE_HEIGHT_MM;
  doc.setDrawColor(PDF_PRIMARY_COLOR);
  doc.roundedRect(
    PDF_PAGE_MARGIN_MM,
    cursorY,
    PDF_CONTENT_WIDTH_MM,
    boxHeight,
    PDF_CODES_BOX_CORNER_RADIUS_MM,
    PDF_CODES_BOX_CORNER_RADIUS_MM
  );

  let codeY = cursorY + PDF_CODES_BOX_PADDING_MM + PDF_CODE_LINE_HEIGHT_MM / 2;

  backupCodes.forEach((code, index) => {
    doc.setFont(PDF_TEXT_FONT_FAMILY, PDF_FONT_STYLE_NORMAL);
    doc.setFontSize(PDF_CODE_INDEX_FONT_SIZE_PT);
    doc.setTextColor(PDF_MUTED_TEXT_COLOR);
    doc.text(`${index + 1}.`, PDF_PAGE_MARGIN_MM + PDF_CODES_BOX_PADDING_MM, codeY);

    doc.setFont(PDF_CODE_FONT_FAMILY, PDF_FONT_STYLE_BOLD);
    doc.setFontSize(PDF_CODE_FONT_SIZE_PT);
    doc.setTextColor(PDF_BODY_TEXT_COLOR);
    doc.text(code, PDF_PAGE_MARGIN_MM + PDF_CODES_BOX_PADDING_MM + PDF_CODE_GUTTER_MM, codeY);

    codeY += PDF_CODE_LINE_HEIGHT_MM;
  });

  doc.save(getBackupCodesFileName());
};
